import { useState, useEffect, useCallback, isValidElement, cloneElement } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiErrorDetail, downloadFile } from "@/lib/api";
import { allDocuments } from "@/lib/tree";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search,
  Save,
  CheckCircle2,
  Sparkles,
  History,
  Download,
  Eye,
  Pencil,
  RefreshCw,
  Loader2,
  FileText,
} from "lucide-react";

const AI_ACTIONS = [
  ["continue", "Continue"],
  ["rewrite", "Rewrite"],
  ["expand", "Expand"],
  ["shorten", "Shorten"],
  ["simplify", "Simplify"],
  ["make_technical", "Make Technical"],
  ["add_examples", "Add Examples"],
  ["add_references", "Add References"],
];

const EXPORT_FORMATS = [
  ["pdf", "PDF"],
  ["md", "Markdown"],
  ["docx", "DOCX"],
  ["html", "HTML"],
  ["txt", "Plain text"],
];

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Recursively wrap case-insensitive matches of `term` in <mark> across react-markdown children.
function highlightNodes(node, term, keyBase) {
  if (!term) return node;
  if (typeof node === "string") {
    const parts = node.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
    if (parts.length === 1) return node;
    return parts.map((part, i) =>
      part && part.toLowerCase() === term.toLowerCase() ? (
        <mark key={`${keyBase}-${i}`} className="rounded-[2px] bg-primary/25 px-0.5 text-inherit">
          {part}
        </mark>
      ) : (
        part
      )
    );
  }
  if (Array.isArray(node)) return node.map((n, i) => highlightNodes(n, term, `${keyBase}-${i}`));
  if (isValidElement(node) && node.props && node.props.children) {
    return cloneElement(node, { key: node.key ?? keyBase }, highlightNodes(node.props.children, term, keyBase));
  }
  return node;
}


// Stable color per Source_System so documents can be scanned by origin.
const SOURCE_HUES = [18, 200, 150, 42, 280, 340, 100, 250];
function sourceHue(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SOURCE_HUES[h % SOURCE_HUES.length];
}
function SourceTag({ source, testid }) {
  if (!source) return null;
  const hue = sourceHue(source);
  return (
    <span
      data-testid={testid}
      className="inline-flex max-w-[160px] items-center truncate rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
      style={{
        backgroundColor: `hsl(${hue} 55% 45% / 0.14)`,
        color: `hsl(${hue} 60% 38%)`,
        borderColor: `hsl(${hue} 55% 45% / 0.35)`,
      }}
      title={source}
    >
      {source}
    </span>
  );
}


export default function DocumentsPanel({ projectId, tree, reloadTree }) {
  const docs = allDocuments(tree);
  const sources = [...new Set(docs.map((d) => d.source).filter(Boolean))].sort();
  const [selectedId, setSelectedId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState("");
  const [view, setView] = useState("split");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [results, setResults] = useState(null); // backend content-search results
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!selectedId && docs.length) setSelectedId(docs[0].id);
  }, [docs, selectedId]);

  const loadDoc = useCallback(async (id) => {
    const { data } = await api.get(`/nodes/${id}`);
    setDoc(data);
    setContent(data.content || "");
    setDirty(false);
  }, []);

  useEffect(() => {
    if (selectedId) loadDoc(selectedId);
  }, [selectedId, loadDoc]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/nodes/${selectedId}`, { content, status: "reviewing" });
      await loadDoc(selectedId);
      await reloadTree();
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    await api.put(`/nodes/${selectedId}`, { status: "approved" });
    await loadDoc(selectedId);
    await reloadTree();
    toast.success("Approved");
  };

  const regenerate = async () => {
    await api.post(`/projects/${projectId}/generate`, {
      scope: "single",
      node_ids: [selectedId],
      mode: doc?.mode || undefined,
    });
    toast.success("Regenerating… check back in a moment");
    setTimeout(() => loadDoc(selectedId), 6000);
    setTimeout(() => {
      loadDoc(selectedId);
      reloadTree();
    }, 14000);
  };

  const runAiAction = async (action) => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/nodes/${selectedId}/ai-action`, { action });
      setDoc(data);
      setContent(data.content || "");
      setDirty(false);
      await reloadTree();
      toast.success("Applied");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const restoreVersion = async (v) => {
    setContent(v.content);
    setDirty(true);
    toast.info("Version loaded into editor — save to keep it");
  };

  const doExport = async (fmt) => {
    try {
      await downloadFile(`/nodes/${selectedId}/export?format=${fmt}`, `${doc.title}.${fmt}`);
    } catch {
      toast.error("Export failed");
    }
  };

  // Full-text content search (title + body, with snippets) — debounced.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/search`, { params: { q } });
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, projectId]);

  const contentSearch = query.trim().length >= 2;
  const titleFiltered = query
    ? docs.filter((d) => d.title.toLowerCase().includes(query.toLowerCase()))
    : docs;
  const preList = contentSearch ? results || [] : titleFiltered;
  const listItems = sourceFilter === "all" ? preList : preList.filter((d) => d.source === sourceFilter);

  // When a content search is active, highlight the matched term inside the preview.
  const searchTerm = contentSearch ? query.trim() : "";
  const mkHl = (Tag) => {
    const Comp = ({ node, children, ...props }) => <Tag {...props}>{highlightNodes(children, searchTerm, Tag)}</Tag>;
    Comp.displayName = `Hl(${Tag})`;
    return Comp;
  };
  const hlComponents = searchTerm
    ? {
        p: mkHl("p"), li: mkHl("li"), h1: mkHl("h1"), h2: mkHl("h2"),
        h3: mkHl("h3"), h4: mkHl("h4"), strong: mkHl("strong"), em: mkHl("em"),
        blockquote: mkHl("blockquote"), td: mkHl("td"), th: mkHl("th"),
      }
    : undefined;

  if (docs.length === 0) {
    return (
      <div className="animate-fade-in rounded-sm border border-dashed border-border py-20 text-center text-muted-foreground">
        No documents yet. Build the index and generate content first.
      </div>
    );
  }

  return (
    <div className="animate-fade-in grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
      {/* List */}
      <div className="rounded-sm border border-border bg-card">
        <div className="border-b border-border p-3">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles & contents…"
              className="rounded-sm pl-8"
              data-testid="doc-search-input"
            />
          </div>
          {sources.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="source-filter">
              <button
                onClick={() => setSourceFilter("all")}
                data-testid="source-filter-all"
                className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  sourceFilter === "all" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                All · {docs.length}
              </button>
              {sources.map((s) => {
                const hue = sourceHue(s);
                const on = sourceFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => setSourceFilter(on ? "all" : s)}
                    data-testid={`source-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className="max-w-[160px] truncate rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors"
                    style={{
                      backgroundColor: `hsl(${hue} 55% 45% / ${on ? 0.22 : 0.1})`,
                      color: `hsl(${hue} 60% 38%)`,
                      borderColor: `hsl(${hue} 55% 45% / ${on ? 0.6 : 0.3})`,
                    }}
                    title={s}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <ScrollArea className="h-[560px]">
          <div className="p-2">
            {contentSearch && searching && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground" data-testid="search-loading">
                <Loader2 size={12} className="animate-spin" /> Searching all documents…
              </div>
            )}
            {contentSearch && !searching && listItems.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="search-empty">
                No documents match “{query.trim()}”.
              </div>
            )}
            {listItems.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                data-testid={`doc-list-item-${d.id}`}
                className={`mb-1 flex w-full items-start gap-2 rounded-sm px-3 py-2 text-left transition-colors ${
                  selectedId === d.id ? "bg-secondary" : "hover:bg-secondary/50"
                }`}
              >
                <FileText size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{d.title}</div>
                  {contentSearch && d.snippet ? (
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" data-testid={`search-snippet-${d.id}`}>
                      {d.snippet}
                    </div>
                  ) : null}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={d.status || "waiting"} />
                    <SourceTag source={d.source} testid={`doc-source-${d.id}`} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Editor */}
      <div className="rounded-sm border border-border bg-card">
        {!doc ? (
          <div className="flex h-[600px] items-center justify-center text-muted-foreground">
            Select a document
          </div>
        ) : (
          <div className="flex h-full flex-col">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
              <div className="mr-auto flex min-w-0 items-center gap-2">
                <span className="truncate font-display text-lg font-semibold">{doc.title}</span>
                <StatusBadge status={doc.status || "waiting"} />
                <SourceTag source={doc.source} testid="doc-detail-source" />
              </div>

              <div className="flex rounded-sm border border-border">
                {[
                  ["edit", Pencil],
                  ["split", FileText],
                  ["preview", Eye],
                ].map(([v, Icon]) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    data-testid={`view-${v}`}
                    className={`px-2.5 py-1.5 ${view === v ? "bg-secondary" : ""}`}
                    title={v}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-sm" disabled={aiBusy} data-testid="ai-actions-trigger">
                    {aiBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />}
                    AI actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Transform document</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {AI_ACTIONS.map(([id, label]) => (
                    <DropdownMenuItem key={id} onClick={() => runAiAction(id)} data-testid={`ai-action-${id}`}>
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-sm" data-testid="doc-history-trigger">
                    <History size={15} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Version history</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(doc.versions || []).length === 0 ? (
                    <div className="px-2 py-2 text-sm text-muted-foreground">No versions yet</div>
                  ) : (
                    [...doc.versions].reverse().map((v, i) => (
                      <DropdownMenuItem key={i} onClick={() => restoreVersion(v)}>
                        {v.label} · {new Date(v.created_at).toLocaleString()}
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-sm" data-testid="doc-export-trigger">
                    <Download size={15} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {EXPORT_FORMATS.map(([fmt, label]) => (
                    <DropdownMenuItem key={fmt} onClick={() => doExport(fmt)} data-testid={`doc-export-${fmt}`}>
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="ghost" size="sm" className="rounded-sm" onClick={regenerate} data-testid="doc-regenerate" title="Regenerate">
                <RefreshCw size={15} />
              </Button>
              <Button size="sm" className="rounded-sm" onClick={save} disabled={saving || !dirty} data-testid="doc-save">
                {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Save size={15} className="mr-1" />}
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm"
                onClick={approve}
                disabled={doc.status === "approved"}
                data-testid="doc-approve"
              >
                <CheckCircle2 size={15} className="mr-1" /> Approve
              </Button>
            </div>

            {doc.status === "failed" && (
              <div
                className="flex items-start gap-2 border-b border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                data-testid="doc-error-banner"
              >
                <span className="font-medium">Generation failed:</span>
                <span className="text-destructive/90">
                  {doc.error || "Unknown error."} — click Regenerate to retry, or re-run “Missing / failed only” from the Generate tab.
                </span>
              </div>
            )}

            {/* Canvas */}
            <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-2" style={{ minHeight: 520 }}>
              {(view === "edit" || view === "split") && (
                <Textarea
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="# Start writing in Markdown…"
                  data-testid="doc-editor-textarea"
                  className={`h-full min-h-[520px] resize-none rounded-none border-0 font-mono text-sm leading-relaxed focus-visible:ring-0 ${
                    view === "split" ? "border-r border-border lg:col-span-1" : "lg:col-span-2"
                  }`}
                />
              )}
              {(view === "preview" || view === "split") && (
                <ScrollArea className={`h-[520px] ${view === "preview" ? "lg:col-span-2" : ""}`}>
                  <div className="mx-auto max-w-3xl px-8 py-8">
                    {content.trim() ? (
                      <div className="prose-espresso" data-testid="doc-preview">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={hlComponents}>{content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Nothing generated yet.</p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
